import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons/faExpand";
import { EditableFormAttributeValueProperty } from "../../Atoms/EditableProperties/EditableFormAttributeValueProperty";
import { InputHelp } from "../../Atoms/inputs/InputHelp";

const FIRST_COLUMN_WIDTH = 240;
const ANSWER_COLUMN_WIDTH = 120;

export const parseGridAnswer = (value) => {
  if (!value) {
    return {};
  }

  try {
    const answer = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(answer.rows)) {
      return {};
    }

    return answer.rows.reduce((selections, row) => {
      if (
        row &&
        typeof row.rowId === "string" &&
        Array.isArray(row.selectedColumnIds)
      ) {
        selections[row.rowId] = row.selectedColumnIds
          .filter((id) => typeof id === "string")
          .slice(0, 1);
      }
      return selections;
    }, {});
  } catch (error) {
    return {};
  }
};

export const serializeGridAnswer = (configuration, selections) =>
  JSON.stringify({
    rows: (configuration?.rows || [])
      .map((row) => ({
        rowId: row.id,
        selectedColumnIds: (selections[row.id] || []).slice(0, 1),
      }))
      .filter((row) => row.selectedColumnIds.length > 0),
  });

export const EditableGrid = ({
  finishCallback,
  value: initialValue,
  patchField,
  configuration,
  disabled,
}) => {
  const [selections, setSelections] = useState(() =>
    parseGridAnswer(initialValue),
  );
  const [error, setError] = useState(undefined);
  const [expanded, setExpanded] = useState(false);
  const selectionsRef = useRef(selections);
  const lastPersistedSelectionsRef = useRef(selections);
  const queuedSelectionsRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!savingRef.current && queuedSelectionsRef.current === null) {
      const persistedSelections = parseGridAnswer(initialValue);
      selectionsRef.current = persistedSelections;
      lastPersistedSelectionsRef.current = persistedSelections;
      setSelections(persistedSelections);
    }
  }, [initialValue]);

  const rows = configuration?.rows || [];
  const columns = configuration?.columns || [];

  const persistQueuedSelections = () => {
    if (savingRef.current || queuedSelectionsRef.current === null) {
      return;
    }

    const selectionsToPersist = queuedSelectionsRef.current;
    queuedSelectionsRef.current = null;
    savingRef.current = true;

    finishCallback({
      [patchField]: serializeGridAnswer(configuration, selectionsToPersist),
    })
      .then(() => {
        lastPersistedSelectionsRef.current = selectionsToPersist;
        savingRef.current = false;
        persistQueuedSelections();
      })
      .catch((saveError) => {
        savingRef.current = false;
        queuedSelectionsRef.current = null;
        selectionsRef.current = lastPersistedSelectionsRef.current;
        setSelections(lastPersistedSelectionsRef.current);
        setError(saveError.message);
      });
  };

  const toggle = (rowId, columnId) => {
    if (disabled) {
      return;
    }

    const selectedColumnIds = selectionsRef.current[rowId] || [];
    const nextSelectedColumnIds = selectedColumnIds.includes(columnId)
      ? []
      : [columnId];
    const nextSelections = {
      ...selectionsRef.current,
      [rowId]: nextSelectedColumnIds,
    };

    selectionsRef.current = nextSelections;
    setSelections(nextSelections);
    setError(undefined);
    queuedSelectionsRef.current = nextSelections;
    persistQueuedSelections();
  };

  if (rows.length === 0 || columns.length === 0) {
    return null;
  }

  const renderGrid = (isExpanded = false) => {
    const firstColumnWidth = isExpanded ? 300 : FIRST_COLUMN_WIDTH;
    const answerColumnWidth = isExpanded ? 140 : ANSWER_COLUMN_WIDTH;

    return (
      <ScrollArea
        data-testid={isExpanded ? "expanded-grid-scroll" : "grid-scroll"}
        data-scrollbars="always"
        type="always"
        offsetScrollbars
        scrollbarSize={12}
        sx={{ fontFamily: '"RobotoRegular", Roboto, sans-serif' }}
        styles={{
          scrollbar: {
            backgroundColor: "#F1F1F1",
            "&[data-orientation='vertical']": { display: "none" },
          },
          thumb: {
            backgroundColor: "#ADB5BD",
            borderRadius: 6,
          },
        }}
      >
        <Table
          sx={{
            minWidth: firstColumnWidth + columns.length * answerColumnWidth,
            tableLayout: "fixed",
            "& th, & td": {
              border: "none",
              fontFamily: '"RobotoRegular", Roboto, sans-serif',
            },
            "& .mantine-Text-root": {
              fontFamily: '"RobotoRegular", Roboto, sans-serif',
            },
            "& thead th": { color: "#141517", whiteSpace: "normal" },
            "& tbody th": { padding: "12px 16px" },
            "& tbody tr:nth-of-type(odd)": { backgroundColor: "#F1F1F1" },
          }}
        >
          <colgroup>
            <col style={{ width: firstColumnWidth }} />
            {columns.map((column) => (
              <col key={column.id} style={{ width: answerColumnWidth }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th />
              {columns.map((column) => (
                <th key={column.id} style={{ textAlign: "center" }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th>
                  <Text size="sm" weight={600}>
                    {row.label}
                  </Text>
                  {row.description && (
                    <Text size="xs" color="dimmed" weight={400} mt={2}>
                      {row.description}
                    </Text>
                  )}
                </th>
                {columns.map((column) => (
                  <td key={column.id}>
                    <Group position="center">
                      <Checkbox
                        aria-label={`${row.label}: ${column.label}`}
                        checked={(selections[row.id] || []).includes(column.id)}
                        disabled={disabled}
                        onChange={() => toggle(row.id, column.id)}
                      />
                    </Group>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea>
    );
  };

  return (
    <>
      <Group position="right" mb={4}>
        <Tooltip label="Raster vergrößern" withArrow>
          <ActionIcon
            aria-label="Raster vergrößern"
            onClick={() => setExpanded(true)}
            variant="subtle"
            size="sm"
          >
            <FontAwesomeIcon icon={faExpand} />
          </ActionIcon>
        </Tooltip>
      </Group>

      {renderGrid()}

      <Modal
        opened={expanded}
        onClose={() => setExpanded(false)}
        title="Checkbox-Raster"
        size="calc(100vw - 48px)"
        centered
      >
        <Box sx={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}>
          {renderGrid(true)}
        </Box>
      </Modal>

      {error ? <InputHelp errorDescription={error} /> : null}
    </>
  );
};

EditableGrid.propTypes = {
  finishCallback: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  patchField: PropTypes.string.isRequired,
  configuration: PropTypes.object,
  disabled: PropTypes.bool,
};

const AnswerGrid = ({
  formSubmissionId,
  patchField,
  configuration,
  values,
  handleChange,
  disabled,
}) => (
  <EditableFormAttributeValueProperty
    formSubmissionId={formSubmissionId}
    patchField={patchField}
    changeHandler={(patch) => handleChange(patchField, patch[patchField])}
    entity={values}
    editFormatter={EditableGrid}
    configuration={configuration}
    editOnly={true}
    disabled={disabled}
  />
);

AnswerGrid.propTypes = {
  formSubmissionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  patchField: PropTypes.string.isRequired,
  configuration: PropTypes.object,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default AnswerGrid;
